/**
 * Client notices (R190, decision 1010 item 1, MB 3 Nov 2026; docs/specs/cloud/alerts.md §11,
 * docs/specs/portal/client-area.md §12): the people of a client organisation are told about their own projects, by
 * email and by browser push, as each of them chooses on the client portal's "Notices" page.
 *
 * - Kinds (`CLIENT_NOTICE_KINDS`): a new report, a new document (a Safety document, a share link or the refly list
 *   shown to the client), the project's status, a block completed, the daily line progress, and flight started and
 *   landed for an aircraft assigned to the project. Never SOS, lost tracking, an overdue flight, a landing soon, an
 *   exceedance or any other staff alert (decision 64: clients see position, take-off, flight time and landing; never
 *   SOS or lost tracking): only `flight_started` and `flight_ended` of the staff kinds ever become a client notice
 *   (`clientNoticeFromAlert`).
 * - Defaults: reports by email and push, documents by push only (decision 959: no email when a Safety document is
 *   shown, unless the person asks for one), status and blocks by email and push, the daily line progress by push,
 *   flights off. A report's email is the report email Teslyn already sends (app-email.md §2): its choice is the
 *   person's `report_email_choice`, so a report never brings two emails.
 * - Limits: at most `CLIENT_NOTICE_EMAILS_PER_DAY` emails and `CLIENT_NOTICE_PUSHES_PER_DAY` push notices a person in
 *   24 hours (Resend's free plan sends 100 emails a day for every organisation, decision 740).
 * - Words: the operator's own name (the tenant's), the project's number and title, times in the project's time.
 *
 * The notify Edge Function sends them (supabase/functions/_shared/client-notices.ts) and runs the progress watch
 * (`clientProgressDue`, `clientProgressNotices`); the database raises the rest from what staff publish
 * (20261103233000_client_notices.sql). Built for the Edge Functions by tools/dev/copy-edge-shared.mjs: every type an
 * export names is declared here.
 */
import { approvalNoticeSubject } from './client-approvals.js';
import { appEmailFrom } from './email-sender.js';
import { formatKm } from './km.js';
import type { Block, LineStatus, PlannedLine } from './model.js';
import { projectProgress } from './portal.js';
import { reportLabel, periodText, type ReportLabelKind } from './report-label.js';
import { projectUtcOffsetH } from './settings.js';
import { acceptedM } from './tjr-adapter.js';

// ------------------------------------------------------------------------------------------------ kinds

export const CLIENT_NOTICE_KIND_IDS = [
  'report_published',
  'document_shown',
  'project_status',
  'block_completed',
  'line_summary',
  'flight_started',
  'flight_landed',
  'approval_requested',
] as const;
export type ClientNoticeKindId = (typeof CLIENT_NOTICE_KIND_IDS)[number];

export type ClientNoticeGroup = 'Reports and documents' | 'Progress' | 'Flights' | 'Approvals';

export interface ClientNoticeKind {
  readonly id: ClientNoticeKindId;
  readonly group: ClientNoticeGroup;
  /** The Notices page's name for it. */
  readonly label: string;
  /** What brings one, in a line. */
  readonly description: string;
  /** Sent by push unless the person says otherwise. */
  readonly push: boolean;
  /** Sent by email unless the person says otherwise. */
  readonly email: boolean;
  /**
   * Who sends the email: `notice` (this notice's own email) or `report_email` (the report email of app-email.md §2,
   * whose choice is `report_email_choice`; the notice itself then goes by push only).
   */
  readonly emailBy: 'notice' | 'report_email';
  /** How long a push notice is worth delivering. */
  readonly ttlSeconds: number;
}

const HOUR_S = 3600;

export const CLIENT_NOTICE_KINDS: readonly ClientNoticeKind[] = [
  {
    id: 'report_published',
    group: 'Reports and documents',
    label: 'New reports',
    description: 'A daily or weekly report is published to you.',
    push: true,
    email: true,
    emailBy: 'report_email',
    ttlSeconds: 24 * HOUR_S,
  },
  {
    id: 'document_shown',
    group: 'Reports and documents',
    label: 'New documents',
    description:
      'A safety document (the risk assessment, a signed JSA or JSP, the hazards KML), a safety register entry, a data share link or the refly list is shown to you.',
    push: true,
    // decision 959: no email when a Safety document is shown, unless the person asks for one here
    email: false,
    emailBy: 'notice',
    ttlSeconds: 24 * HOUR_S,
  },
  {
    id: 'project_status',
    group: 'Progress',
    label: 'Project status',
    description: 'The project is mobilising, under way (Active) or complete.',
    push: true,
    email: true,
    emailBy: 'notice',
    ttlSeconds: 24 * HOUR_S,
  },
  {
    id: 'block_completed',
    group: 'Progress',
    label: 'Block completed',
    description: 'A survey block has no km left to fly.',
    push: true,
    email: true,
    emailBy: 'notice',
    ttlSeconds: 24 * HOUR_S,
  },
  {
    id: 'line_summary',
    group: 'Progress',
    label: 'Daily line progress',
    description:
      'Each morning: the lines flown the day before, how many were accepted, and the project’s progress.',
    push: true,
    email: false,
    emailBy: 'notice',
    ttlSeconds: 12 * HOUR_S,
  },
  {
    id: 'flight_started',
    group: 'Flights',
    label: 'Flight started',
    description: 'An aircraft assigned to your project takes off.',
    push: false,
    email: false,
    emailBy: 'notice',
    ttlSeconds: HOUR_S,
  },
  {
    id: 'flight_landed',
    group: 'Flights',
    label: 'Flight landed',
    description: 'An aircraft assigned to your project lands, with its flight time.',
    push: false,
    email: false,
    emailBy: 'notice',
    ttlSeconds: HOUR_S,
  },
  {
    // R191 (decision 1010 item 2): a request sent for the client's approval; a contract to the client admins only
    id: 'approval_requested',
    group: 'Approvals',
    label: 'Approval asked',
    description:
      'Something is sent for your approval: a survey block change, the JSA, the JSP or the risk assessment; or, for a client admin, a contract to sign.',
    push: true,
    email: true,
    emailBy: 'notice',
    ttlSeconds: 24 * HOUR_S,
  },
];

export function isClientNoticeKind(v: unknown): v is ClientNoticeKindId {
  return typeof v === 'string' && (CLIENT_NOTICE_KIND_IDS as readonly string[]).includes(v);
}

export function clientNoticeKind(id: string): ClientNoticeKind | null {
  return CLIENT_NOTICE_KINDS.find((k) => k.id === id) ?? null;
}

/**
 * The staff alerts that also become a client notice, and as which kind: the tracker poller's take-off and landing of
 * an aircraft, for the projects it is assigned to (decision 64). Every other staff kind (SOS, lost tracking, tracking
 * resumed, overdue flight, landing soon, the diurnal, station, storm and crystal pack kinds) never does. The database
 * trigger `client_notice_from_alert` holds the same two (compared in supabase/tests/rls-client-notices.test.ts).
 */
export const CLIENT_NOTICE_FROM_ALERT: Readonly<Record<string, ClientNoticeKindId>> = {
  flight_started: 'flight_started',
  flight_ended: 'flight_landed',
};

export function clientNoticeFromAlert(alertKind: string): ClientNoticeKindId | null {
  return Object.prototype.hasOwnProperty.call(CLIENT_NOTICE_FROM_ALERT, alertKind)
    ? CLIENT_NOTICE_FROM_ALERT[alertKind]!
    : null;
}

/** The project statuses the client is told about (Planned is where a project starts; Archived leaves the portal). */
export const CLIENT_NOTICE_STATUSES = ['mobilising', 'active', 'complete'] as const;

/** The roles that receive client notices: every person of the client organisation. */
export const CLIENT_NOTICE_ROLES = ['client_admin', 'client_viewer'] as const;

/** Emails and push notices one person receives at most in 24 hours (each channel). */
export const CLIENT_NOTICE_EMAILS_PER_DAY = 10;
export const CLIENT_NOTICE_PUSHES_PER_DAY = 30;
/** A report or document notice waits this long, so one published by mistake and withdrawn at once is never sent. */
export const CLIENT_NOTICE_HOLD_S = 120;
/** Tries of one notice (within a day of being raised), as the alerts and the report emails. */
export const CLIENT_NOTICE_MAX_TRIES = 5;
/** The daily line progress is sent at this hour of the project's time, for the day before. */
export const LINE_SUMMARY_HOUR = 7;
/** A daily line progress more than this late (the cloud was down) is not sent. */
export const LINE_SUMMARY_LATE_MS = 12 * 3600_000;
/** The Notices page lists the notices of the last this many days. */
export const CLIENT_NOTICE_RECENT_DAYS = 30;

// ------------------------------------------------------------------------------------------------ words

/** What the database stores of one notice (`client_notice`), with the names the words need. */
export interface ClientNotice {
  readonly id: string;
  readonly kind: string;
  /** Null only for a contract's approval made before its project (R191). */
  readonly project_id: string | null;
  readonly occurred_at: string;
  readonly detail: Readonly<Record<string, unknown>>;
}

export interface ClientNoticeContext {
  /** The operator: the tenant's name ("Thomson Airborne"). */
  readonly tenantName: string;
  readonly projectNumber: string;
  readonly projectTitle: string;
  /** The project's overrides (its UTC offset, `projectUtcOffsetH`); null: UTC+8. */
  readonly qcOverrides?: Readonly<Record<string, unknown>> | null;
}

export interface ClientNoticeWords {
  readonly title: string;
  readonly body: string;
}

export const CLIENT_NOTICE_TITLE_MAX = 120;
export const CLIENT_NOTICE_BODY_MAX = 600;

const clip = (s: string, n: number): string => (s.length <= n ? s : `${s.slice(0, n - 1)}…`);
const str = (v: unknown): string => (typeof v === 'string' ? v : '');
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "UTC+8", "UTC+9.5", "UTC-3", "UTC". */
export function utcOffsetText(offsetH: number): string {
  if (offsetH === 0) return 'UTC';
  return `UTC${offsetH > 0 ? '+' : '-'}${Math.abs(offsetH)}`;
}

/** "14:22 (UTC+8)": a moment in the project's time. */
export function projectClockText(ms: number, offsetH: number): string {
  const d = new Date(ms + offsetH * 3600_000);
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  return `${hh}:${mm} (${utcOffsetText(offsetH)})`;
}

/** "16 Oct 2026": the project's date of a moment. */
export function projectDateText(ms: number, offsetH: number): string {
  const d = new Date(ms + offsetH * 3600_000);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** The project's date (yyyy-mm-dd) of a moment. */
export function projectDate(ms: number, offsetH: number): string {
  return new Date(ms + offsetH * 3600_000).toISOString().slice(0, 10);
}

const SAFETY_WORDS: Readonly<Record<string, string>> = {
  risk_assessment: 'risk assessment',
  jsa: 'signed JSA',
  jsp: 'signed JSP',
  hazards_kml: 'hazards KML',
};

/** A register entry's kind in a notice (decision 1023), as the domain's REGISTER_KIND_WORDS, in lower case. */
const REGISTER_NOTICE_WORDS: Readonly<Record<string, string>> = {
  accident: 'accident',
  incident: 'incident',
  near_miss: 'near miss',
  landholder_complaint: 'landholder complaint',
  below_lower_working_limit: 'below lower working limit',
  below_hard_deck: 'below hard deck',
};

const STATUS_WORDS: Readonly<Record<string, string>> = {
  mobilising: 'Mobilising',
  active: 'Active',
  complete: 'Complete',
};

const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`;

/**
 * The title and words of one notice, built from what the database stored (facts only; the words are here). Plain
 * text; the project's time with its offset ("14:22 (UTC+8)"). Never a tracker's device, account or provider, never a
 * revision number (R139), never an internal state.
 */
export function clientNoticeWords(n: ClientNotice, ctx: ClientNoticeContext): ClientNoticeWords {
  const offsetH = projectUtcOffsetH({ qc_overrides: ctx.qcOverrides ?? null });
  const project = `${ctx.projectNumber} ${ctx.projectTitle}`.trim();
  const org = ctx.tenantName.trim() || 'Your survey company';
  const d = n.detail;
  const at = Date.parse(n.occurred_at);
  const words = ((): ClientNoticeWords => {
    switch (n.kind) {
      case 'report_published': {
        const kind = (str(d.report_kind) || 'daily') as ReportLabelKind;
        const label = reportLabel({ kind, period: str(d.period), revision: num(d.revision) ?? 1 });
        const again = (num(d.revision) ?? 1) > 1 ? ' It replaces the earlier copy.' : '';
        return {
          title: `${project}: ${label}`,
          body: `${org} has published ${label} for ${project}. Open it under Reports.${again}`,
        };
      }
      case 'document_shown': {
        const source = str(d.source);
        if (source === 'link') {
          const label = str(d.label) || 'a data share link';
          return {
            title: `${project}: ${label}`,
            body: `${org} has shared “${label}” with you for ${project}. Open it under Deliverables.`,
          };
        }
        if (source === 'refly') {
          return {
            title: `${project}: Refly list`,
            body: `${org} has published the refly list for ${project}. Open it on the project’s Refly list tab.`,
          };
        }
        if (source === 'register') {
          // decision 1023: a safety register entry shown to the client; the kind only, never the entry's words
          const kind = REGISTER_NOTICE_WORDS[str(d.register_kind)] ?? 'safety register entry';
          return {
            title: `${project}: Safety register`,
            body: `${org} has shown you a safety register entry (${kind}) for ${project}. Open it on the Safety register page.`,
          };
        }
        const what = SAFETY_WORDS[str(d.doc_kind)] ?? 'safety document';
        const newer = (num(d.version) ?? 1) > 1 ? 'a newer ' : 'the ';
        return {
          title: `${project}: ${what.charAt(0).toUpperCase()}${what.slice(1)}`,
          body: `${org} has shown you ${newer}${what} for ${project}. Open it on the project’s Safety documents tab.`,
        };
      }
      case 'project_status': {
        const to = str(d.to);
        const word = STATUS_WORDS[to] ?? to;
        const body =
          to === 'complete'
            ? `${org} has marked ${project} complete.`
            : to === 'mobilising'
              ? `${org} has set ${project} to Mobilising: the crew and aircraft are getting ready to fly.`
              : `${org} has set ${project} to Active: the survey is under way.`;
        return { title: `${project} is now ${word}`, body };
      }
      case 'block_completed': {
        const block = str(d.block_name) || 'A block';
        const planned = num(d.planned_km);
        const percent = num(d.percent);
        const remaining = num(d.remaining_km);
        const parts = [
          `Block ${block} of ${project} has no km left to fly${planned !== null ? ` (${formatKm(planned)} km planned)` : ''}.`,
        ];
        if (percent !== null && remaining !== null)
          parts.push(`The project is ${percent}% complete, ${formatKm(remaining)} km remaining.`);
        return { title: `${project}: block ${block} completed`, body: parts.join(' ') };
      }
      case 'line_summary': {
        const day = str(d.day);
        const dayText = periodText(day);
        const flown = num(d.flown) ?? 0;
        const accepted = num(d.accepted) ?? 0;
        const partial = num(d.partial) ?? 0;
        const rejected = num(d.rejected) ?? 0;
        const pending = num(d.pending) ?? 0;
        const km = num(d.accepted_km) ?? 0;
        const parts = [`${accepted} accepted (${formatKm(km)} km)`];
        if (partial > 0) parts.push(`${partial} accepted in part`);
        if (rejected > 0) parts.push(`${rejected} to be reflown`);
        if (pending > 0) parts.push(`${pending} not reviewed yet`);
        const percent = num(d.percent);
        const remaining = num(d.remaining_km);
        const tail =
          percent !== null && remaining !== null
            ? ` The project is ${percent}% complete, ${formatKm(remaining)} km remaining.`
            : '';
        return {
          title: `${project}: ${plural(accepted, 'line')} accepted on ${dayText}`,
          body: `From the flights of ${dayText}: ${plural(flown, 'line')} flown, ${parts.join(', ')}.${tail}`,
        };
      }
      case 'flight_started': {
        const label = str(d.label) || 'The aircraft';
        const t = Date.parse(str(d.takeoff_at));
        const when = Number.isFinite(t) ? t : at;
        return {
          title: `Flight started: ${label}`,
          body: `${label} took off at ${projectClockText(when, offsetH)} on ${projectDateText(when, offsetH)}, flying for ${project}.`,
        };
      }
      case 'flight_landed': {
        const label = str(d.label) || 'The aircraft';
        const t = Date.parse(str(d.landing_at));
        const when = Number.isFinite(t) ? t : at;
        const hours = num(d.hours);
        return {
          title: `Flight landed: ${label}`,
          body: `${label} landed at ${projectClockText(when, offsetH)} on ${projectDateText(when, offsetH)}${
            hours !== null ? ` after ${hours.toFixed(1)} h` : ''
          }, flying for ${project}.`,
        };
      }
      case 'approval_requested': {
        const kind = str(d.approval_kind) || 'block_change';
        const title = str(d.title) || 'An item';
        const contract = kind === 'contract';
        const where = project ? ` (${project})` : '';
        return {
          // R191's subject line, "F26048: please approve <title>"
          title: approvalNoticeSubject(
            'approval_requested',
            { kind: kind as never, title, state: 'waiting' },
            ctx.projectNumber,
          ),
          body: `${org} has sent ${title}${where} for your ${contract ? 'signature' : 'approval'}. Open it under Approvals.`,
        };
      }
      default:
        return { title: project, body: `${org} has news about ${project}.` };
    }
  })();
  return {
    title: clip(words.title, CLIENT_NOTICE_TITLE_MAX),
    body: clip(words.body, CLIENT_NOTICE_BODY_MAX),
  };
}

/**
 * Where a notice opens in the client portal: Reports on the report's project for a report (`?page=reports&project=<id>`,
 * R198), Deliverables for a share link, the Safety register page on the project for a register entry (R220), else the
 * project on the tab the notice is about
 * (`?page=projects&project=<id>&tab=<tab>`).
 */
export function clientNoticeLink(
  origin: string,
  n: Pick<ClientNotice, 'kind' | 'project_id' | 'detail'>,
): string {
  const base = origin.replace(/\/+$/, '');
  if (n.kind === 'report_published')
    return n.project_id === null
      ? `${base}/?page=reports`
      : `${base}/?page=reports&project=${encodeURIComponent(n.project_id)}`;
  if (n.kind === 'approval_requested' || n.project_id === null) return `${base}/?page=approvals`;
  if (n.kind === 'document_shown' && str(n.detail.source) === 'link') return `${base}/?page=deliverables`;
  // R220 (decision 1037): register entries live on the client's Safety register page, opened on the project
  if (n.kind === 'document_shown' && str(n.detail.source) === 'register')
    return `${base}/?page=safety-register&project=${encodeURIComponent(n.project_id)}`;
  const tab =
    n.kind === 'document_shown'
      ? str(n.detail.source) === 'refly'
        ? 'refly'
        : 'safety'
      : n.kind === 'flight_started' || n.kind === 'flight_landed'
        ? 'map'
        : 'progress';
  return `${base}/?page=projects&project=${encodeURIComponent(n.project_id)}&tab=${tab}`;
}

// ------------------------------------------------------------------------------------------------ choices and recipients

export interface ClientNoticeMember {
  readonly user_id: string;
  readonly email: string | null;
  /** Their role in the client organisation; absent: a viewer. */
  readonly role?: string | null;
}

/**
 * Whether a person of the client organisation may be told of this notice: a contract sent to sign is for the client
 * admins only (R191, decision 130: the client's admin signs); everything else for every person.
 */
export function clientNoticeForRole(
  kind: string,
  detail: Readonly<Record<string, unknown>> | null | undefined,
  role: string | null | undefined,
): boolean {
  if (kind === 'approval_requested' && detail?.approval_kind === 'contract') return role === 'client_admin';
  return true;
}

/** A person's own choice for one kind in one client organisation: null is the kind's default. */
export interface ClientNoticePreference {
  readonly user_id: string;
  readonly kind: string;
  readonly push: boolean | null;
  readonly email: boolean | null;
}

/**
 * What a person receives of one kind: their own choice, else the kind's default. A report's email is the report
 * email's choice (`report_email_choice.reports`, on unless turned off).
 */
export function clientNoticeChoice(
  kindId: string,
  pref: Pick<ClientNoticePreference, 'push' | 'email'> | null | undefined,
  reportEmails?: boolean | null,
): { readonly push: boolean; readonly email: boolean } {
  const kind = clientNoticeKind(kindId);
  if (!kind) return { push: false, email: false };
  const push = pref?.push ?? kind.push;
  const email = kind.emailBy === 'report_email' ? (reportEmails ?? true) : (pref?.email ?? kind.email);
  return { push, email };
}

export interface ClientNoticeRecipient {
  readonly user_id: string;
  readonly email: string | null;
  readonly push: boolean;
  readonly sendEmail: boolean;
  /** Why a channel the person chose is not sent: the 24-hour limit. */
  readonly limited: readonly ('push' | 'email')[];
  readonly reason: 'choice' | 'staging';
}

/** What each person was sent in the last 24 hours, per channel. */
export interface ClientNoticeSentToday {
  readonly user_id: string;
  readonly channel: 'push' | 'email';
  readonly n: number;
}

/**
 * Who receives one notice and how: every member of the client organisation, by their own choice or the kind's
 * default; a report's email is never this notice's (the report email sends it, app-email.md §2). Within the 24-hour
 * limits. While the staging-only switch is on (decision 975), only the listed sign-ins that are members, by push and
 * email (a report still by push only), whatever they chose.
 */
export function clientNoticeRecipients(input: {
  readonly kind: string;
  /** The notice's facts (a contract's approval goes to client admins only). */
  readonly detail?: Readonly<Record<string, unknown>> | null;
  readonly members: readonly ClientNoticeMember[];
  readonly preferences: readonly ClientNoticePreference[];
  readonly sentToday?: readonly ClientNoticeSentToday[];
  readonly staging?: readonly string[] | null;
}): ClientNoticeRecipient[] {
  const kind = clientNoticeKind(input.kind);
  if (!kind) return [];
  const staging =
    input.staging && input.staging.length > 0
      ? new Set(input.staging.map((e) => e.trim().toLowerCase()))
      : null;
  const count = (user: string, channel: 'push' | 'email'): number =>
    (input.sentToday ?? [])
      .filter((s) => s.user_id === user && s.channel === channel)
      .reduce((a, s) => a + s.n, 0);
  const out: ClientNoticeRecipient[] = [];
  const seen = new Set<string>();
  for (const m of [...input.members].sort((a, b) => a.user_id.localeCompare(b.user_id))) {
    if (seen.has(m.user_id)) continue;
    seen.add(m.user_id);
    if (!clientNoticeForRole(kind.id, input.detail, m.role)) continue;
    const email = m.email && m.email.includes('@') ? m.email : null;
    let push: boolean;
    let mail: boolean;
    let reason: ClientNoticeRecipient['reason'];
    if (staging) {
      if (!email || !staging.has(email.trim().toLowerCase())) continue;
      push = true;
      mail = kind.emailBy === 'notice';
      reason = 'staging';
    } else {
      const pref = input.preferences.find((p) => p.user_id === m.user_id && p.kind === kind.id) ?? null;
      const c = clientNoticeChoice(kind.id, pref);
      push = c.push;
      mail = kind.emailBy === 'notice' && c.email && email !== null;
      reason = 'choice';
    }
    const limited: ('push' | 'email')[] = [];
    if (push && count(m.user_id, 'push') >= CLIENT_NOTICE_PUSHES_PER_DAY) {
      push = false;
      limited.push('push');
    }
    if (mail && count(m.user_id, 'email') >= CLIENT_NOTICE_EMAILS_PER_DAY) {
      mail = false;
      limited.push('email');
    }
    if (push || mail || limited.length > 0)
      out.push({ user_id: m.user_id, email, push, sendEmail: mail, limited, reason });
  }
  return out;
}

/** The error a delivery records when the 24-hour limit kept it back. */
export function clientNoticeLimitWords(channel: 'push' | 'email'): string {
  return channel === 'email'
    ? `Not sent: ${CLIENT_NOTICE_EMAILS_PER_DAY} notice emails in 24 hours is the most a person receives.`
    : `Not sent: ${CLIENT_NOTICE_PUSHES_PER_DAY} push notices in 24 hours is the most a person receives.`;
}

// ------------------------------------------------------------------------------------------------ push and email

/** The service worker's payload (`apps/web/public/teslyn-sw.js`; the shape of the alerts' `NoticePushPayload`). */
export interface ClientNoticePushPayload {
  readonly v: 1;
  readonly kind: string;
  readonly level: 'normal';
  readonly title: string;
  readonly body: string;
  readonly at: number;
  readonly tag: string;
  readonly url: string;
  /** Clients acknowledge nothing: never an Acknowledge button. */
  readonly ack: null;
  readonly test: false;
}

export function clientNoticePushPayload(
  n: Pick<ClientNotice, 'id' | 'kind' | 'occurred_at'>,
  words: ClientNoticeWords,
  openUrl: string,
): ClientNoticePushPayload {
  const at = Date.parse(n.occurred_at);
  return {
    v: 1,
    kind: `client:${n.kind}`,
    level: 'normal',
    title: clip(words.title, CLIENT_NOTICE_TITLE_MAX),
    body: clip(words.body, CLIENT_NOTICE_BODY_MAX),
    at: Number.isFinite(at) ? at : 0,
    tag: `teslyn:client:${n.id}`,
    url: openUrl,
    ack: null,
    test: false,
  };
}

/** The staging-only mark (decision 975), as the alerts': "Staging: F26048 Milgun is now Active". */
export const CLIENT_NOTICE_STAGING_PREFIX = 'Staging: ';

export function clientNoticeStagingTitle(title: string): string {
  return title.startsWith(CLIENT_NOTICE_STAGING_PREFIX) ? title : `${CLIENT_NOTICE_STAGING_PREFIX}${title}`;
}

export interface ClientNoticeEmail {
  readonly from: string;
  readonly subject: string;
  readonly text: string;
  readonly html: string;
}

const escapeHtml = (s: string): string =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

/**
 * The email of one notice, from the operator's own sender (decision 890: "No Reply <organisation> powered by
 * Teslyn" <no-reply@teslyn.au>), plain text and the same words as escaped HTML, with where to change the choice.
 */
export function clientNoticeEmail(
  words: ClientNoticeWords,
  opts: { readonly tenantName: string; readonly openUrl: string | null },
): ClientNoticeEmail {
  const org = opts.tenantName.trim() || 'your survey company';
  const open = opts.openUrl
    ? `Open it in ${org}'s client portal: ${opts.openUrl}`
    : `Open ${org}'s client portal to see it.`;
  const why = `You receive this because of your choices on the Notices page of ${org}'s client portal. Change them there.`;
  const text = [words.body, '', open, '', why].join('\n');
  const html = [
    `<p>${escapeHtml(words.body)}</p>`,
    opts.openUrl
      ? `<p><a href="${escapeHtml(opts.openUrl)}">Open it in ${escapeHtml(org)}'s client portal</a></p>`
      : `<p>${escapeHtml(open)}</p>`,
    `<p><small>${escapeHtml(why)}</small></p>`,
  ].join('\n');
  return { from: appEmailFrom(opts.tenantName), subject: clip(words.title, 150), text, html };
}

// ------------------------------------------------------------------------------------------------ the progress watch

/** The watch's memory of one project (`client_progress_watch`); null before its first look. */
export interface ClientProgressWatch {
  readonly checked_at: string;
  /** The blocks that had no km left at the last look. */
  readonly completed_blocks: readonly string[];
  /** The last project day whose line progress was sent or found empty (yyyy-mm-dd). */
  readonly summary_day: string | null;
}

/** One project the watch may look at (`client_progress_candidates()`). */
export interface ClientProgressCandidate {
  readonly project_id: string;
  readonly tenant_id: string;
  readonly client_org_id: string;
  readonly number: string;
  readonly title: string;
  readonly qc_overrides: Readonly<Record<string, unknown>> | null;
  /** The newest change of its lines' status or blocks; null with none. */
  readonly changed_at: string | null;
  readonly watch: ClientProgressWatch | null;
}

export interface ClientProgressDue {
  /** Its blocks changed since the last look (or there was none). */
  readonly blocks: boolean;
  /** The project day whose line progress is due now, or null. */
  readonly summaryDay: string | null;
  /** When that day's progress is due (07:00 project time the day after), ms. */
  readonly summaryAtMs: number | null;
}

/**
 * Whether the watch looks at a project now: when its line status or blocks changed since the last look, and once a
 * day at `LINE_SUMMARY_HOUR` project time for the day before (not when more than `LINE_SUMMARY_LATE_MS` late).
 */
export function clientProgressDue(c: ClientProgressCandidate, nowMs: number): ClientProgressDue {
  const offsetH = projectUtcOffsetH({ qc_overrides: c.qc_overrides });
  const changed = c.changed_at ? Date.parse(c.changed_at) : NaN;
  const blocks = c.watch === null || (Number.isFinite(changed) && changed > Date.parse(c.watch.checked_at));
  const today = projectDate(nowMs, offsetH);
  const [y, m, d] = today.split('-').map(Number) as [number, number, number];
  const atMs = Date.UTC(y, m - 1, d, LINE_SUMMARY_HOUR) - offsetH * 3600_000;
  const day = new Date(Date.UTC(y, m - 1, d - 1)).toISOString().slice(0, 10);
  const due =
    nowMs >= atMs &&
    nowMs - atMs <= LINE_SUMMARY_LATE_MS &&
    (c.watch?.summary_day === null || c.watch?.summary_day === undefined || c.watch.summary_day < day);
  return { blocks, summaryDay: due ? day : null, summaryAtMs: due ? atMs : null };
}

/** The rows the watch reads of a project (`client_progress_data`): its live and removed rows as synced. */
export interface ClientProgressData {
  readonly blocks: readonly Readonly<Record<string, unknown>>[];
  readonly plan: readonly Readonly<Record<string, unknown>>[];
  readonly statuses: readonly Readonly<Record<string, unknown>>[];
  /** Each flight's id and date (the project's date). */
  readonly flights: readonly { readonly id: string; readonly date: string }[];
}

/** One notice the watch raises (`client_notice_raise`). */
export interface ClientNoticeRaise {
  readonly tenant_id: string;
  readonly project_id: string;
  readonly client_org_id: string;
  readonly kind: ClientNoticeKindId;
  readonly notice_key: string;
  readonly occurred_at: string;
  readonly detail: Readonly<Record<string, unknown>>;
}

export interface LineSummary {
  /** Line statuses of the day's flights (reflights and patches included). */
  readonly flown: number;
  readonly accepted: number;
  readonly partial: number;
  readonly rejected: number;
  readonly pending: number;
  /** Σ accepted km of the day's statuses (`acceptedM`, the daily report's accepted km of the day). */
  readonly acceptedKm: number;
}

/** The line progress of one project day: the statuses of that day's flights as reviewed now. */
export interface LineSummaryStatus {
  readonly flight_id: string;
  readonly acceptance: 'pending' | 'accepted' | 'partial' | 'rejected';
  readonly accepted_m: number;
  readonly use_km: boolean;
  readonly deleted_at: string | null;
}

export function lineSummaryOf(
  statuses: readonly LineSummaryStatus[],
  flights: readonly { readonly id: string; readonly date: string }[],
  day: string,
): LineSummary {
  const ids = new Set(flights.filter((f) => f.date === day).map((f) => f.id));
  const of = statuses.filter((s) => s.deleted_at === null && ids.has(s.flight_id));
  const n = (a: string): number => of.filter((s) => s.acceptance === a).length;
  return {
    flown: of.length,
    accepted: n('accepted'),
    partial: n('partial'),
    rejected: n('rejected'),
    pending: n('pending'),
    acceptedKm: acceptedM(of) / 1000,
  };
}

const KM_DONE = 0.0005;

/**
 * What the watch raises for one project, and what it remembers: "Block completed" for each block that has no km left
 * now (`projectProgress`: every line accepted, or the block ticked "Job km are completed") and had some at the last
 * look (none at the first look, so a project already flown is not announced block by block); the day's line progress
 * when it is due and a line was flown that day. Keys: `block_completed:<project>:<block>` (once per block, ever) and
 * `line_summary:<project>:<day>`.
 */
export function clientProgressNotices(input: {
  readonly candidate: ClientProgressCandidate;
  readonly data: ClientProgressData;
  readonly due: ClientProgressDue;
  readonly nowMs: number;
}): {
  readonly notices: ClientNoticeRaise[];
  readonly completedBlocks: string[];
  readonly summaryDay: string | null;
} {
  const { candidate: c, data, due } = input;
  const statuses = data.statuses as unknown as LineStatus[];
  const plan = data.plan as unknown as PlannedLine[];
  const blocks = data.blocks as unknown as Block[];
  const p = projectProgress(statuses, plan, blocks);
  const complete = p.blocks.filter((b) => b.plannedKm > 0 && b.remainingKm < KM_DONE);
  const completedBlocks = complete.map((b) => b.blockId).sort();
  const base = { tenant_id: c.tenant_id, project_id: c.project_id, client_org_id: c.client_org_id };
  const percent = p.total.percentComplete;
  const remaining = Math.round(p.total.remainingKm * 100) / 100;
  const notices: ClientNoticeRaise[] = [];
  if (c.watch !== null) {
    const before = new Set(c.watch.completed_blocks);
    for (const b of complete) {
      if (before.has(b.blockId)) continue;
      notices.push({
        ...base,
        kind: 'block_completed',
        notice_key: `block_completed:${c.project_id}:${b.blockId}`,
        occurred_at: new Date(input.nowMs).toISOString(),
        detail: {
          block_id: b.blockId,
          block_name: b.name,
          planned_km: Math.round(b.plannedKm * 100) / 100,
          accepted_km: Math.round(b.acceptedKm * 100) / 100,
          percent,
          remaining_km: remaining,
        },
      });
    }
  }
  let summaryDay = c.watch?.summary_day ?? null;
  if (due.summaryDay !== null && due.summaryAtMs !== null) {
    summaryDay = due.summaryDay;
    const s = lineSummaryOf(statuses, data.flights, due.summaryDay);
    if (s.flown > 0)
      notices.push({
        ...base,
        kind: 'line_summary',
        notice_key: `line_summary:${c.project_id}:${due.summaryDay}`,
        occurred_at: new Date(due.summaryAtMs).toISOString(),
        detail: {
          day: due.summaryDay,
          flown: s.flown,
          accepted: s.accepted,
          partial: s.partial,
          rejected: s.rejected,
          pending: s.pending,
          accepted_km: Math.round(s.acceptedKm * 100) / 100,
          percent,
          remaining_km: remaining,
        },
      });
  }
  return { notices, completedBlocks, summaryDay };
}
